import { useEffect, useRef, useState } from 'react'

// Raised by AuthContext when the fetch interceptor (src/apiClient.js) sees a
// 401 from a same-origin /api/* request while the app believes a user is
// logged in — i.e. the session actually expired server-side. Deliberately
// does NOT navigate anywhere or unmount the page underneath it: the whole
// point (see the incident this fixes) is that a mid-work interruption should
// let the user sign back in over the top of what they were doing and
// continue, not lose the page to a redirect to /login.
//
// Not a hard block, either — Dismiss lets the user keep reading whatever is
// already on screen without forcing a login right this second.
//
// And not a trap: because the username is locked (see below), the only way
// out used to be knowing the expired account's own password. "Sign out and
// use a different account" is the third door — it calls AuthContext's
// logout(), which nulls the user, so ProtectedRoute redirects to /login and
// unmounts the stale page on the way. That unmount is the whole reason
// switching accounts is safe there and not here.
//
// Deliberately no ESC-to-dismiss here, unlike Modal.jsx. Do not add one: ESC
// closing this modal would just leave the page underneath still signed out —
// the next action the user takes 401s again and reopens it. Dismiss (the
// button below) exists for "let me keep reading what's already on screen",
// which is a real, distinct choice a mouse/tap user can make deliberately;
// a reflexive ESC tap is not that, so it's not wired to the same thing.
function SessionExpiredModal({ isOpen, onClose, onLogin, onSignOut, username: expiredUsername }) {
  // Locked to the user whose session actually expired — see username input
  // below. Re-seeded from the prop each time the modal opens so a second
  // expiry (a different account, in principle) doesn't show a stale value.
  const [username, setUsername] = useState(expiredUsername || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const passwordInputRef = useRef(null)

  // Matches Modal.jsx's open/close effect (body-scroll lock while shown,
  // restored on close/unmount) plus focus management Modal.jsx doesn't need
  // (its buttons are the natural focus target; this modal has a form field
  // that should be ready to type into immediately, since focus could be
  // anywhere in the interrupted page when this pops up).
  useEffect(() => {
    if (!isOpen) return
    setUsername(expiredUsername || '')
    setPassword('')
    setError('')
    document.body.style.overflow = 'hidden'
    // Username is prefilled and locked (see below), so the password field is
    // the actual first thing to fill in.
    passwordInputRef.current?.focus()
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, expiredUsername])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    const result = await onLogin(username, password)

    setIsLoading(false)
    if (result.success) {
      setPassword('')
    } else {
      setError(result.error || 'Login failed')
    }
  }

  return (
    <div
      className="modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(5, 10, 18, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Above everything else in the app — including the stacked entity
        // preview modals (10001+) — since this can interrupt the user at any
        // point, mid whatever else is open.
        zIndex: 10050,
        padding: '1rem',
      }}
    >
      <div
        className="modal-content"
        style={{
          background: 'var(--card-bg)',
          border: '2px solid var(--accent-teal)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '2rem', lineHeight: 1 }}>🔒</div>
          <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.35rem', fontWeight: 500 }}>
            Session expired
          </h3>
        </div>

        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          You were signed out after a period of inactivity. Sign back in to keep going — you won't
          lose your place.
        </p>

        {error && (
          <div
            style={{
              background: 'rgba(231, 76, 60, 0.1)',
              border: '1px solid #e74c3c',
              borderRadius: '6px',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#e74c3c',
              fontSize: '0.9rem',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="session-expired-username"
              style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.35rem' }}
            >
              Username
            </label>
            <input
              id="session-expired-username"
              type="text"
              value={username}
              // Locked to the account whose session expired — not editable.
              // Signing back in as a DIFFERENT account here would leave the
              // page underneath still showing the previous user's
              // already-fetched data while the role/permissions flip under
              // it, a confusing mixed state with no server-side leak but no
              // good reason to allow either. Switching accounts is done via
              // the sign-out link below the form instead, which discards that
              // page entirely rather than swapping an identity underneath it.
              // Don't drop this readOnly without removing that link too.
              readOnly
              required
              disabled={isLoading}
              title="Locked to the account that was signed in"
              style={{
                width: '100%',
                padding: '0.65rem 0.85rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--hover-bg)',
                color: 'var(--text-secondary)',
                fontSize: '0.95rem',
                boxSizing: 'border-box',
                cursor: 'not-allowed',
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="session-expired-password"
              style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.35rem' }}
            >
              Password
            </label>
            <input
              id="session-expired-password"
              ref={passwordInputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.65rem 0.85rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-color)',
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              Dismiss
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                background: isLoading ? 'var(--accent-teal-dark)' : 'var(--accent-teal)',
                color: '#ffffff',
                border: '1px solid var(--accent-teal-dark)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                minWidth: '110px',
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>

        <div
          style={{
            borderTop: '1px solid var(--border-color)',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={onSignOut}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.25rem',
              color: 'var(--text-muted)',
              fontSize: '0.85rem',
              textDecoration: 'underline',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  )
}

export default SessionExpiredModal
